import { useNavigate, useParams } from 'react-router';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { useSmartCallDetailsOutletContext } from './CloudConnectorSmartCallProfilesTab.tsx';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { DivideBar } from '../parkingabo-users/OperatorParkingaboUsersForm.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useEasyForm } from 'dg-web-shared/common/utils/FormHooksUtils.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { HeaderCancelSaveButtons } from '../layout/components/HeaderCancelSaveButtons.tsx';
import { GenericFormSubmitError } from '../parkingabo-users/ParkingaboUsersUtils.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { ReactHookFormSwitch } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormSwitch.tsx';
import { Alert, Box } from '@mui/material';
import { useEffect } from 'react';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import { removeWhitespace } from 'dg-web-shared/lib/StringConversions.ts';

enum Fields {
    description = 'description',
    phone = 'phone',
    forwardCallEntry = 'forwardCallEntry',
    forwardCallExit = 'forwardCallExit',
    exitWithOpenAmount = 'exitWithOpenAmount',
    exitWithOtherCases = 'exitWithOtherCases',
    forwardCallPayStation = 'forwardCallPayStation',
    forwardCallAccess = 'forwardCallAccess',
    autoForwardDisruptions = 'autoForwardDisruptions',
    forwardEntryDisruptions = 'forwardEntryDisruptions',
    forwardExitDisruptions = 'forwardExitDisruptions',
}

export type FieldValues = {
    [Fields.description]: string;
    [Fields.phone]: string;
    [Fields.forwardCallEntry]: boolean;
    [Fields.forwardCallExit]: boolean;
    [Fields.exitWithOpenAmount]: boolean;
    [Fields.exitWithOtherCases]: boolean;
    [Fields.forwardCallPayStation]: boolean;
    [Fields.forwardCallAccess]: boolean;
    [Fields.autoForwardDisruptions]: boolean;
    [Fields.forwardEntryDisruptions]: boolean;
    [Fields.forwardExitDisruptions]: boolean;
};

function deriveFormValuesOrUseDefault(
    existingProfileDetails: SmartCallProfileDetails | null,
): FieldValues {
    const formatPhone = (phone: string): string => {
        const digits = removeWhitespace(phone);
        if (!/^\+41\d{9}$/.test(digits)) return phone;
        return digits.replace(
            /^\+41(\d{2})(\d{3})(\d{2})(\d{2})$/,
            '+41 $1 $2 $3 $4',
        );
    };

    return {
        [Fields.description]: existingProfileDetails?.description ?? '',
        [Fields.phone]: existingProfileDetails?.phone
            ? formatPhone(existingProfileDetails.phone)
            : '',
        [Fields.forwardCallEntry]:
            existingProfileDetails?.forwardCallEntry ?? true,
        [Fields.forwardCallExit]:
            existingProfileDetails?.forwardCallExit ?? true,
        [Fields.exitWithOpenAmount]:
            existingProfileDetails?.exitWithOpenAmount ?? true,
        [Fields.exitWithOtherCases]:
            existingProfileDetails?.exitWithOtherCases ?? true,
        [Fields.forwardCallPayStation]:
            existingProfileDetails?.forwardCallPayStation ?? true,
        [Fields.forwardCallAccess]:
            existingProfileDetails?.forwardCallAccess ?? true,
        [Fields.autoForwardDisruptions]:
            existingProfileDetails?.autoForwardDisruptions ?? true,
        [Fields.forwardEntryDisruptions]:
            existingProfileDetails?.forwardEntryDisruptions ?? true,
        [Fields.forwardExitDisruptions]:
            existingProfileDetails?.forwardExitDisruptions ?? true,
    };
}

interface SmartCallProfileDetails {
    id: string;
    description: string;
    phone: string;
    createdAt: string;
    lastChangedAt: string;
    isDefault: boolean;
    forwardCallEntry: boolean;
    forwardCallExit: boolean;
    exitWithOpenAmount: boolean;
    exitWithOtherCases: boolean;
    forwardCallPayStation: boolean;
    forwardCallAccess: boolean;
    autoForwardDisruptions: boolean;
    forwardEntryDisruptions: boolean;
    forwardExitDisruptions: boolean;
}

export function CloudConnectorSmartCallProfileAdd() {
    const { zoneId, refetchProfiles } = useSmartCallDetailsOutletContext();
    const navigate = useNavigate();
    const [createState, createRequest] = useServerWrite<
        FieldValues & { zoneId: string },
        object,
        ValidationData
    >(() => ({
        url: `/ui-api/operator-account/cloud-connector/smart-call/profile`,
    }));

    useServerSuccessEffect(createState, _ => {
        refetchProfiles();
        navigate('..');
    });

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            render={controller => (
                <CloudConnectorSmartCallProfileForm
                    title={
                        <Localized
                            de="Neues Profil"
                            fr="Nouveau profil"
                            it="Nuovo profilo"
                            en="New profile"
                        />
                    }
                    onSubmit={payload =>
                        createRequest({ zoneId: zoneId, ...payload })
                    }
                    submitState={createState}
                    onClose={controller.close}
                    onDirtyStateChange={controller.setShouldConfirmBeforeLeave}
                    profileDetails={null}
                />
            )}
        />
    );
}

export function CloudConnectorSmartCallProfileDetails() {
    const { zoneId, refetchProfiles } = useSmartCallDetailsOutletContext();
    const { profileId } = useParams();

    if (!profileId) {
        throw new Error('Missing profile id');
    }

    const [profileDetailsState, refetchProfileDetails] = useServerFetch<
        SmartCallProfileDetails,
        { profileId: string }
    >(
        ({ profileId }) => ({
            url: `/ui-api/operator-account/cloud-connector/smart-call/profile/${profileId}`,
        }),
        { profileId: profileId },
    );
    const [updateProfileState, updateProfile] = useServerWrite<
        FieldValues & { zoneId: string; profileId: string },
        object,
        ValidationData
    >(_ => ({
        url: `/ui-api/operator-account/cloud-connector/smart-call/profile`,
        method: RequestMethod.PUT,
    }));

    useServerSuccessEffect(updateProfileState, () => {
        refetchProfileDetails();
        refetchProfiles();
    });

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={profileDetailsState}
                    render={profileDetails => (
                        <CloudConnectorSmartCallProfileForm
                            title={profileDetails.description}
                            onSubmit={payload =>
                                updateProfile({
                                    zoneId: zoneId,
                                    profileId: profileId,
                                    ...payload,
                                })
                            }
                            submitState={updateProfileState}
                            onClose={controller.close}
                            onDirtyStateChange={
                                controller.setShouldConfirmBeforeLeave
                            }
                            profileDetails={profileDetails}
                        />
                    )}
                />
            )}
        />
    );
}

interface SmartCallProfileFormProps {
    title: React.ReactNode;
    onSubmit: (payload: FieldValues) => void;
    submitState: ServerRequestState<object, ValidationData>;
    onClose: () => void;
    onDirtyStateChange: (dirty: boolean) => void;
    profileDetails: SmartCallProfileDetails | null;
}

function CloudConnectorSmartCallProfileForm({
    title,
    onSubmit,
    submitState,
    onDirtyStateChange,
    onClose,
    profileDetails,
}: SmartCallProfileFormProps) {
    const { language } = useLanguage();

    const {
        formInfo: { control, handleSubmit, reset, watch, setValue },
        formState: { isDirty },
        genericSubmitError,
    } = useEasyForm(onDirtyStateChange, submitState, language, {
        defaultValues: deriveFormValuesOrUseDefault(profileDetails),
    });

    const isCreate = profileDetails === null;
    const editMode = isDirty || isCreate;
    const isDefaultProfile = profileDetails ? profileDetails.isDefault : false;
    const forwardCallExit = watch(Fields.forwardCallExit);
    const exitWithOpenAmount = watch(Fields.exitWithOpenAmount);
    const exitWithOtherCases = watch(Fields.exitWithOtherCases);

    useEffect(() => {
        if (!exitWithOpenAmount && !exitWithOtherCases) {
            setValue(Fields.forwardCallExit, false);
        }
    }, [exitWithOpenAmount, exitWithOtherCases, setValue]);

    const phoneInvalidCharsMessage = useTranslation({
        de: 'Die Telefonnummer muss im folgenden Format sein: +41 XX XXX XX XX',
        fr: 'Le numéro de téléphone doit être au format suivant : +41 XX XXX XX XX',
        it: 'Il numero di telefono deve essere nel seguente formato: +41 XX XXX XX XX',
        en: 'The phone number must be in the following format: +41 XX XXX XX XX',
    });
    const standardDescriptionInvalid = useTranslation({
        de: 'Das Profil darf nicht "Standard" heissen.',
        fr: 'Le profil ne peut pas s’appeler "Standard".',
        it: 'Il profilo non può chiamarsi "Standard".',
        en: 'The profile cannot be named "Standard".',
    });

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={title}
                    editMode={editMode}
                    buttons={
                        <>
                            {editMode ? (
                                <HeaderCancelSaveButtons
                                    onCancel={isCreate ? onClose : reset}
                                    onSave={handleSubmit(onSubmit)}
                                    loading={
                                        submitState.status ===
                                        RequestStatus.PENDING
                                    }
                                    noDiscardDialog={isCreate}
                                />
                            ) : (
                                <HeaderActionCloseButtons onClose={onClose} />
                            )}
                        </>
                    }
                />
            }
            body={
                <Box
                    sx={{
                        display: 'flex',
                        height: '100%',
                        color: theme => theme.palette.blue.main,
                    }}
                >
                    <FormContentLayout>
                        <FormLeftColumn>
                            <ReactHookFormTextField
                                label={
                                    <Localized
                                        de="Bezeichnung"
                                        fr="Désignation"
                                        it="Descrizione"
                                        en="Description"
                                    />
                                }
                                name={Fields.description}
                                control={control}
                                rules={{
                                    validate: {
                                        isDescriptionValid: (val: string) => {
                                            if (isDefaultProfile) {
                                                return true;
                                            }

                                            const value =
                                                removeWhitespace(
                                                    val,
                                                ).toLowerCase();
                                            if (value === '') {
                                                return false;
                                            }

                                            if (value === 'standard') {
                                                return standardDescriptionInvalid;
                                            }

                                            return true;
                                        },
                                    },
                                }}
                                readonly={isDefaultProfile}
                                trimOnBlur={true}
                            />
                            <ReactHookFormTextField
                                label={
                                    <Localized
                                        de="Telefon"
                                        fr="Téléphone"
                                        it="Telefono"
                                        en="Phone"
                                    />
                                }
                                name={Fields.phone}
                                control={control}
                                rules={{
                                    validate: {
                                        isPhoneValid: (val: string) => {
                                            const value = removeWhitespace(val);
                                            if (value === '') {
                                                return false;
                                            }

                                            if (!/^\+41\d{9}$/.test(value)) {
                                                return phoneInvalidCharsMessage;
                                            }

                                            return true;
                                        },
                                    },
                                }}
                                trimOnBlur={true}
                            />

                            <Alert severity="info">
                                <Localized
                                    de="Wenn an der Ausfahrt kein Anruf an diese Nummer durchgestellt werden kann (Anrufbeantworter oder nach 40 Sekunden nicht abgenommen), öffnet sich die Barriere einmal automatisch."
                                    fr="Si à la sortie un appel à ce numéro ne peut pas être établi (répondeur ou pas de réponse dans les 40 secondes), la barrière s’ouvrira automatiquement une fois."
                                    it="Se all'uscita non è possibile stabilire una comunicazione con questo numero (se risponde la segreteria o non viene risposto entro 40 secondi), la barriera si aprirà automaticamente una volta."
                                    en="If at the exit a call to this number cannot be connected (voicemail or not answered within 40 seconds), the barrier will open automatically once."
                                />
                            </Alert>

                            {genericSubmitError != null && (
                                <GenericFormSubmitError
                                    error={genericSubmitError}
                                    submitState={submitState}
                                />
                            )}
                        </FormLeftColumn>
                        <FormRightColumn spacing={1}>
                            <DivideBar
                                largeMarginTop={true}
                                title={
                                    <Localized
                                        de="Im Normalbetrieb"
                                        fr="En fonctionnement normal"
                                        it="Nel funzionamento normale"
                                        en="In normal operation"
                                    />
                                }
                            />
                            <p>
                                <Localized
                                    de="Gerätetypen, die den Gegensprechanlage-Anruf an die Telefonnummer weiterleiten werden. Bei Deaktivierung wird eine Ansage abgespielt («Die Notrufzentrale ist im Moment nicht besetzt.»)"
                                    fr="Types d'appareils qui transféreront l'appel de l'interphone vers le numéro de téléphone. Si désactivé, un message vocal sera diffusé («La centrale d'urgence n'est actuellement pas sous surveillance.»)."
                                    it="Tipi di dispositivi che inoltreranno la chiamata del citofono al numero di telefono. Se disattivato, verrà riprodotto un messaggio vocale («La centrale d'emergenza non è attualmente presidiata.»)."
                                    en="Device types that will forward the intercom call to the phone number. If disabled, a message will be played («The emergency center is not staffed at the moment.»)."
                                />
                            </p>
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Einfahrt / Durchfahrt"
                                        fr="Entrée / Transit"
                                        it="Entrata / Transito"
                                        en="Entry / Transit"
                                    />
                                }
                                name={Fields.forwardCallEntry}
                                control={control}
                            />
                            <ReactHookFormSwitch
                                label={
                                    <span>
                                        <Localized
                                            de="Ausfahrt"
                                            fr="Sortie"
                                            it="Uscita"
                                            en="Exit"
                                        />
                                        {!forwardCallExit && (
                                            <BarrierOpensWarning />
                                        )}
                                    </span>
                                }
                                name={Fields.forwardCallExit}
                                control={control}
                                onChange={(_, checked) => {
                                    setValue(Fields.forwardCallExit, checked);
                                    setValue(
                                        Fields.exitWithOpenAmount,
                                        checked,
                                    );
                                    setValue(
                                        Fields.exitWithOtherCases,
                                        checked,
                                    );
                                }}
                            />
                            {forwardCallExit && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        paddingLeft: '50px',
                                        marginTop: '0px !important',
                                    }}
                                >
                                    <ReactHookFormSwitch
                                        label={
                                            <span>
                                                <Localized
                                                    de="mit offenem Betrag"
                                                    fr="avec montant impayé"
                                                    it="con importo aperto"
                                                    en="with outstanding amount"
                                                />
                                                {!exitWithOpenAmount && (
                                                    <BarrierOpensWarning />
                                                )}
                                            </span>
                                        }
                                        name={Fields.exitWithOpenAmount}
                                        control={control}
                                    />
                                    <ReactHookFormSwitch
                                        label={
                                            <span>
                                                <Localized
                                                    de="andere Fälle"
                                                    fr="autres cas"
                                                    it="altri casi"
                                                    en="other cases"
                                                />
                                                {!exitWithOtherCases && (
                                                    <BarrierOpensWarning />
                                                )}
                                            </span>
                                        }
                                        name={Fields.exitWithOtherCases}
                                        control={control}
                                    />
                                </Box>
                            )}
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Kasse"
                                        fr="Caisse"
                                        it="Cassa"
                                        en="Pay station"
                                    />
                                }
                                name={Fields.forwardCallPayStation}
                                control={control}
                            />
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Access"
                                        fr="Accès"
                                        it="Accesso"
                                        en="Access"
                                    />
                                }
                                name={Fields.forwardCallAccess}
                                control={control}
                            />

                            <DivideBar
                                largeMarginTop={true}
                                title={
                                    <Localized
                                        de="Bei Störungen"
                                        fr="En cas de panne"
                                        it="In caso di guasti"
                                        en="In case of malfunctions"
                                    />
                                }
                            />
                            <p>
                                <Localized
                                    de="Der Gegensprechanlage-Anruf wird immer an die Telefonnummer weitergeleitet."
                                    fr="L'appel de l'interphone est toujours transféré au numéro de téléphone."
                                    it="La chiamata del citofono viene sempre trasferita al numero di telefono."
                                    en="The intercom call is always forwarded to the phone number."
                                />
                            </p>
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Störungen automatisch melden"
                                        fr="Signaler automatiquement les pannes"
                                        it="Segnalare automaticamente i guasti"
                                        en="Automatically report malfunctions"
                                    />
                                }
                                name={Fields.autoForwardDisruptions}
                                control={control}
                            />

                            {/**
                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Dauerhaft offen ganzes Parking (bei Einfahrtsstörungen)"
                                        fr="Ouverte en permanence pour tout le parking (en cas de pannes à l'entrée)"
                                        it="Apertura permanente per tutto il parcheggio (in caso di guasti all'entrata)"
                                        en="Permanently open for the entire parking (in case of entry malfunctions)"
                                    />
                                }
                                name={Fields.forwardEntryDisruptions}
                                control={control}
                            />

                            <ReactHookFormSwitch
                                label={
                                    <Localized
                                        de="Barriere öffnet sich mit der Präsenz (bei Ausfahrtsstörungen)"
                                        fr="La barrière s'ouvre avec la présence (en cas de pannes à la sortie)"
                                        it="La barriera si apre con la presenza (in caso di guasti all'uscita)"
                                        en="Barrier opens on presence (in case of exit malfunctions)"
                                    />
                                }
                                name={Fields.forwardExitDisruptions}
                                control={control}
                            />
                            */}
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'end',
                                }}
                            >
                                {profileDetails && (
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Erstellt am"
                                                fr="Créé le"
                                                it="Creato il"
                                                en="Created at"
                                            />
                                        }
                                        value={Formatter.dayMonthYearHourMinute(
                                            DateTime.fromISO(
                                                profileDetails.createdAt,
                                            ),
                                        )}
                                    />
                                )}
                            </Box>
                        </FormRightColumn>
                    </FormContentLayout>
                </Box>
            }
        />
    );
}

function BarrierOpensWarning() {
    return (
        <Box
            sx={{
                color: theme => theme.palette.warning.main,
                display: 'inline',
            }}
        >
            <Localized
                de={` ${EmDash} Barriere öffnet sich (1x)`}
                fr={` ${EmDash} La barrière s’ouvre (1x)`}
                it={` ${EmDash} La barriera si apre (1x)`}
                en={` ${EmDash} Barrier opens (1x)`}
            />
        </Box>
    );
}
